import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { PageHeader } from '@/shared/components/PageHeader';
import { Spinner } from '@/shared/components/Spinner';
import { Button } from '@/shared/components/Button';
import { Modal } from '@/shared/components/Modal';
import { Input } from '@/shared/components/Input';
import { ApiError } from '@/lib/api-client';
import { useMaterialsAdmin, useMaterialFolders, useDeleteMaterial, useUpdateMaterial, useUploadMaterial, useCreateFolder, useUpdateFolder, useDeleteFolder, } from '../hooks/useMaterialsAdmin';
import { MaterialViewerModal } from '../components/MaterialViewerModal';
import { useOppositions } from '../hooks/useOppositions';
import { Breadcrumb } from '../components/Breadcrumb';
import { FolderCard } from '../components/FolderCard';
import { FileRow } from '../components/FileRow';
import { BulkBar } from '../components/BulkBar';
import { UploadDropZone } from '../components/UploadDropZone';
import { UploadProgressList } from '../components/UploadProgressList';
import { SearchResults } from '../components/SearchResults';
import { PermissionsModal } from '../components/PermissionsModal';
import { FolderPermissionsModal } from '../components/FolderPermissionsModal';
import { FolderProfessorPermissionsModal } from '../components/FolderProfessorPermissionsModal';
import { BulkPermissionsModal } from '../components/BulkPermissionsModal';
import { RenameModal } from '../components/RenameModal';
import { NewFolderModal } from '../components/NewFolderModal';
import { MoveToFolderModal } from '../components/MoveToFolderModal';
import { buildFolderTree, filesInFolder, getAncestors, indexFolders, isDescendantOrSame, } from '../utils/tree';
import { normalize } from '../utils/normalize';
const ALLOWED_EXTENSIONS = ['.pdf'];
const MAX_FILE_SIZE = 50 * 1024 * 1024;
export function MaterialsAdminPage() {
    const { data: oppositions = [], isLoading: oppLoading } = useOppositions();
    const { data: materials = [], isLoading: matLoading, isError: matError } = useMaterialsAdmin();
    const { data: folders = [], isLoading: folLoading } = useMaterialFolders();
    // ── Opposition selector ────────────────────────────────────────────────────
    const enabledOppositions = useMemo(() => oppositions.filter((o) => o.is_enabled), [oppositions]);
    const [oppositionId, setOppositionId] = useState('');
    useEffect(() => {
        if (oppositionId || enabledOppositions.length === 0)
            return;
        // Pick the first enabled opposition that has any materials/folders, otherwise the first enabled.
        const withContent = enabledOppositions.find((o) => materials.some((m) => m.opposition_id === o.id) ||
            folders.some((f) => f.opposition_id === o.id));
        setOppositionId((withContent ?? enabledOppositions[0])?.id ?? '');
    }, [enabledOppositions, materials, folders, oppositionId]);
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [query, setQuery] = useState('');
    const [selectedFolders, setSelectedFolders] = useState(new Set());
    const [selectedFiles, setSelectedFiles] = useState(new Set());
    // Modal state
    const [permissionsMaterial, setPermissionsMaterial] = useState(null);
    const [permissionsFolder, setPermissionsFolder] = useState(null);
    const [professorPermissionsFolder, setProfessorPermissionsFolder] = useState(null);
    const [bulkPermissionsOpen, setBulkPermissionsOpen] = useState(false);
    const [renameMaterial, setRenameMaterial] = useState(null);
    const [renameFolder, setRenameFolder] = useState(null);
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [moveDialog, setMoveDialog] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    // Upload jobs
    const [jobs, setJobs] = useState([]);
    const [viewerMaterial, setViewerMaterial] = useState(null);
    const fileInputRef = useRef(null);
    const folderIndex = useMemo(() => indexFolders(folders), [folders]);
    // Reset selection / current folder when opposition changes
    useEffect(() => {
        setCurrentFolderId(null);
        setQuery('');
        setSelectedFolders(new Set());
        setSelectedFiles(new Set());
    }, [oppositionId]);
    // Mutations
    const uploadMaterial = useUploadMaterial();
    const updateMaterial = useUpdateMaterial();
    const deleteMaterial = useDeleteMaterial();
    const createFolder = useCreateFolder();
    const updateFolder = useUpdateFolder();
    const deleteFolder = useDeleteFolder();
    // ── Derived data ──────────────────────────────────────────────────────────
    const tree = useMemo(() => (oppositionId ? buildFolderTree(folders, materials, oppositionId) : []), [folders, materials, oppositionId]);
    const ancestors = useMemo(() => getAncestors(currentFolderId, folderIndex), [currentFolderId, folderIndex]);
    const visibleFolders = useMemo(() => {
        if (currentFolderId === null) {
            // root: top-level folders for the current opposition
            return tree.map((n) => n);
        }
        function find(nodes) {
            for (const n of nodes) {
                if (n.folder.id === currentFolderId)
                    return n.children;
                const found = find(n.children);
                if (found)
                    return found;
            }
            return null;
        }
        return find(tree) ?? [];
    }, [tree, currentFolderId]);
    const visibleFiles = useMemo(() => {
        if (currentFolderId === null) {
            // root: only show files that have no folder belonging to this opposition (none in our schema)
            return [];
        }
        return filesInFolder(materials, currentFolderId);
    }, [materials, currentFolderId]);
    const searchResults = useMemo(() => {
        const q = normalize(query.trim());
        if (!q || !oppositionId)
            return null;
        return materials
            .filter((m) => m.opposition_id === oppositionId)
            .filter((m) => {
            const path = ancestorsString(m.folder_id, folderIndex);
            const haystack = `${m.name} ${path} ${extensionOf(m.mime_type)}`;
            return normalize(haystack).includes(q);
        })
            .sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true }))
            .slice(0, 200);
    }, [materials, oppositionId, query, folderIndex]);
    const isSearching = !!searchResults;
    // ── Selection ─────────────────────────────────────────────────────────────
    function toggleFolderSel(id, sel) {
        setSelectedFolders((prev) => {
            const next = new Set(prev);
            if (sel)
                next.add(id);
            else
                next.delete(id);
            return next;
        });
    }
    function toggleFileSel(id, sel) {
        setSelectedFiles((prev) => {
            const next = new Set(prev);
            if (sel)
                next.add(id);
            else
                next.delete(id);
            return next;
        });
    }
    function clearSelection() {
        setSelectedFolders(new Set());
        setSelectedFiles(new Set());
    }
    // ── Navigation ────────────────────────────────────────────────────────────
    function navigateToFolder(id) {
        setCurrentFolderId(id);
        setQuery('');
        clearSelection();
    }
    // ── Upload ────────────────────────────────────────────────────────────────
    const handleFiles = useCallback((files) => {
        if (!currentFolderId) {
            alert('Entra en una carpeta antes de subir archivos.');
            return;
        }
        if (!oppositionId)
            return;
        for (const file of files) {
            const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                setJobs((prev) => [
                    ...prev,
                    {
                        id: `${Date.now()}-${Math.random()}`,
                        fileName: file.name,
                        status: 'error',
                        errorMessage: 'Formato no permitido. Sube solo PDF.',
                    },
                ]);
                continue;
            }
            if (file.size > MAX_FILE_SIZE) {
                setJobs((prev) => [
                    ...prev,
                    {
                        id: `${Date.now()}-${Math.random()}`,
                        fileName: file.name,
                        status: 'error',
                        errorMessage: 'Excede 50 MB.',
                    },
                ]);
                continue;
            }
            const jobId = `${Date.now()}-${Math.random()}`;
            setJobs((prev) => [
                ...prev,
                { id: jobId, fileName: file.name, status: 'uploading' },
            ]);
            uploadMaterial.mutate({
                file,
                folder_id: currentFolderId,
                opposition_id: oppositionId,
            }, {
                onSuccess: () => setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status: 'done' } : j))),
                onError: (err) => setJobs((prev) => prev.map((j) => j.id === jobId
                    ? {
                        ...j,
                        status: 'error',
                        errorMessage: err instanceof ApiError ? err.message : 'Error de subida',
                    }
                    : j)),
            });
        }
    }, [currentFolderId, oppositionId, uploadMaterial]);
    // ── Folder CRUD ───────────────────────────────────────────────────────────
    function handleCreateFolder(name) {
        if (!oppositionId)
            return;
        createFolder.mutate({
            name,
            opposition_id: oppositionId,
            parent_id: currentFolderId,
        }, { onSuccess: () => setNewFolderOpen(false) });
    }
    function handleRenameFolder(name) {
        if (!renameFolder)
            return;
        updateFolder.mutate({ id: renameFolder.id, name }, { onSuccess: () => setRenameFolder(null) });
    }
    function handleRenameMaterial(name) {
        if (!renameMaterial)
            return;
        updateMaterial.mutate({ id: renameMaterial.id, name }, { onSuccess: () => setRenameMaterial(null) });
    }
    // ── Move ──────────────────────────────────────────────────────────────────
    async function handleMoveSubmit(targetFolderId) {
        if (!moveDialog)
            return;
        const { materials: moveMaterials, folders: moveFolders } = moveDialog;
        try {
            // Materials: target must be a folder (root not allowed).
            if (moveMaterials.length > 0 && !targetFolderId)
                return;
            if (targetFolderId) {
                await Promise.all(moveMaterials.map((m) => updateMaterial.mutateAsync({ id: m.id, folder_id: targetFolderId })));
            }
            // Folders: parent_id can be null (root).
            await Promise.all(moveFolders.map((f) => updateFolder.mutateAsync({ id: f.id, parent_id: targetFolderId })));
            setMoveDialog(null);
            clearSelection();
        }
        catch {
            // Mutations already surface their own errors via React Query state;
            // falling through here keeps the dialog open without noise.
        }
    }
    // ── Delete ────────────────────────────────────────────────────────────────
    async function handleConfirmDelete() {
        if (!confirmDelete)
            return;
        setDeleteError(null);
        try {
            await Promise.all(confirmDelete.materials.map((m) => deleteMaterial.mutateAsync(m.id)));
            await Promise.all(confirmDelete.folders.map((f) => deleteFolder.mutateAsync(f.id)));
            setConfirmDelete(null);
            clearSelection();
        }
        catch (err) {
            const message = err instanceof ApiError ? err.message : 'Algo falló al eliminar. Vuelve a intentarlo.';
            setDeleteError(message);
        }
    }
    // ── Preview ───────────────────────────────────────────────────────────────
    function handlePreview(material) {
        setViewerMaterial(material);
    }
    // ── Bulk action helpers ───────────────────────────────────────────────────
    const selectedFolderObjs = useMemo(() => folders.filter((f) => selectedFolders.has(f.id)), [folders, selectedFolders]);
    const selectedFileObjs = useMemo(() => materials.filter((m) => selectedFiles.has(m.id)), [materials, selectedFiles]);
    const moveDisabledIds = useMemo(() => {
        const ids = new Set();
        // For each selected folder, disable itself and its descendants in the move-target list.
        for (const f of selectedFolderObjs) {
            for (const candidate of folders) {
                if (isDescendantOrSame(f.id, candidate.id, folderIndex)) {
                    ids.add(candidate.id);
                }
            }
        }
        return ids;
    }, [selectedFolderObjs, folders, folderIndex]);
    const isLoading = oppLoading || matLoading || folLoading;
    const hasSelection = selectedFolders.size > 0 || selectedFiles.size > 0;
    const currentLabel = ancestors[ancestors.length - 1]?.name ?? 'Raíz';
    if (isLoading && materials.length === 0) {
        return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Materiales" }), _jsx("div", { className: "flex h-64 items-center justify-center", children: _jsx(Spinner, { size: "lg" }) })] }));
    }
    if (matError) {
        return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Materiales" }), _jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: "Error al cargar los materiales." })] }));
    }
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Materiales", action: _jsxs("div", { className: "flex items-center gap-2", children: [enabledOppositions.length > 1 && (_jsx("select", { value: oppositionId, onChange: (e) => setOppositionId(e.target.value), className: "rounded-lg border px-2 py-1.5 text-sm", style: {
                                background: 'var(--surface)',
                                borderColor: 'var(--border)',
                                color: 'var(--fg)',
                            }, children: enabledOppositions.map((o) => (_jsx("option", { value: o.id, children: o.name }, o.id))) })), _jsx(Button, { variant: "secondary", onClick: () => setNewFolderOpen(true), children: "+ Carpeta" }), _jsx(Button, { onClick: () => fileInputRef.current?.click(), disabled: !currentFolderId, title: !currentFolderId ? 'Entra en una carpeta para subir archivos' : undefined, children: "+ Subir" }), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: ".pdf,application/pdf", className: "hidden", onChange: (e) => {
                                const files = Array.from(e.target.files ?? []);
                                if (files.length > 0)
                                    handleFiles(files);
                                e.target.value = '';
                            } })] }) }), _jsxs("div", { className: "mb-4 flex flex-col gap-3", children: [_jsx(Input, { type: "search", placeholder: "Buscar archivo en toda la oposici\u00F3n\u2026", value: query, onChange: (e) => setQuery(e.target.value) }), !isSearching && (_jsx(Breadcrumb, { ancestors: ancestors, rootLabel: enabledOppositions.find((o) => o.id === oppositionId)?.name ?? 'Materiales', onNavigate: navigateToFolder }))] }), hasSelection && (_jsx(BulkBar, { selectedFiles: selectedFiles.size, selectedFolders: selectedFolders.size, onMove: () => setMoveDialog({ materials: selectedFileObjs, folders: selectedFolderObjs }), onPermissions: () => setBulkPermissionsOpen(true), onDelete: () => setConfirmDelete({ materials: selectedFileObjs, folders: selectedFolderObjs }), onClear: clearSelection })), _jsx(UploadDropZone, { disabled: !currentFolderId, onFiles: handleFiles, children: isSearching && searchResults ? (_jsx(SearchResults, { results: searchResults, query: query, folderIndex: folderIndex, selected: selectedFiles, busyId: null, onSelectChange: toggleFileSel, onPreview: handlePreview, onPermissions: setPermissionsMaterial, onRename: setRenameMaterial, onMove: (m) => setMoveDialog({ materials: [m], folders: [] }), onDelete: (m) => setConfirmDelete({ materials: [m], folders: [] }), onJumpToFolder: (folderId) => {
                        setQuery('');
                        navigateToFolder(folderId);
                    } })) : (_jsx(FolderContents, { visibleFolders: visibleFolders, visibleFiles: visibleFiles, currentFolderId: currentFolderId, currentLabel: currentLabel, selectedFolders: selectedFolders, selectedFiles: selectedFiles, previewBusyId: null, onOpenFolder: (id) => navigateToFolder(id), onSelectFolder: toggleFolderSel, onSelectFile: toggleFileSel, onRenameFolder: setRenameFolder, onDeleteFolder: (folder) => setConfirmDelete({ materials: [], folders: [folder] }), onPermisosFolder: setPermissionsFolder, onPermisosProfesoresFolder: setProfessorPermissionsFolder, onPreview: handlePreview, onPermissions: setPermissionsMaterial, onRenameMaterial: setRenameMaterial, onMoveMaterial: (m) => setMoveDialog({ materials: [m], folders: [] }), onDeleteMaterial: (m) => setConfirmDelete({ materials: [m], folders: [] }), onUploadClick: () => fileInputRef.current?.click(), onCreateFolder: () => setNewFolderOpen(true) })) }), _jsx(UploadProgressList, { jobs: jobs, onClose: () => setJobs((prev) => prev.filter((j) => j.status === 'uploading')) }), _jsx(MaterialViewerModal, { endpoint: viewerMaterial ? `/materials/${viewerMaterial.id}/preview` : null, downloadEndpoint: viewerMaterial ? `/materials/${viewerMaterial.id}/download` : null, title: viewerMaterial?.name ?? '', onClose: () => setViewerMaterial(null) }), _jsx(PermissionsModal, { material: permissionsMaterial, onClose: () => setPermissionsMaterial(null) }), _jsx(FolderPermissionsModal, { folder: permissionsFolder, onClose: () => setPermissionsFolder(null) }), _jsx(FolderProfessorPermissionsModal, { folder: professorPermissionsFolder, onClose: () => setProfessorPermissionsFolder(null) }), _jsx(BulkPermissionsModal, { open: bulkPermissionsOpen, materials: selectedFileObjs, onClose: () => setBulkPermissionsOpen(false), onDone: () => {
                    setBulkPermissionsOpen(false);
                    clearSelection();
                } }), _jsx(RenameModal, { open: !!renameMaterial, title: "Renombrar archivo", initialValue: renameMaterial?.name ?? '', onClose: () => setRenameMaterial(null), onSubmit: handleRenameMaterial, loading: updateMaterial.isPending }), _jsx(RenameModal, { open: !!renameFolder, title: "Renombrar carpeta", initialValue: renameFolder?.name ?? '', onClose: () => setRenameFolder(null), onSubmit: handleRenameFolder, loading: updateFolder.isPending }), _jsx(NewFolderModal, { open: newFolderOpen, parentLabel: currentLabel, onClose: () => setNewFolderOpen(false), onSubmit: handleCreateFolder, loading: createFolder.isPending }), moveDialog && oppositionId && (_jsx(MoveToFolderModal, { open: true, rootLabel: enabledOppositions.find((o) => o.id === oppositionId)?.name ?? 'Raíz', oppositionId: oppositionId, folders: folders.filter((f) => f.opposition_id === oppositionId), disabledIds: moveDisabledIds, allowRoot: moveDialog.materials.length === 0, onClose: () => setMoveDialog(null), onSubmit: handleMoveSubmit, loading: updateMaterial.isPending || updateFolder.isPending })), _jsx(Modal, { open: !!confirmDelete, onClose: () => {
                    setConfirmDelete(null);
                    setDeleteError(null);
                }, title: "Confirmar eliminaci\u00F3n", size: "sm", children: confirmDelete && (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsxs("p", { className: "text-sm", style: { color: 'var(--fg-2)' }, children: [confirmDelete.materials.length > 0 && confirmDelete.folders.length > 0
                                    ? `Vas a eliminar ${confirmDelete.materials.length} archivos y ${confirmDelete.folders.length} carpetas.`
                                    : confirmDelete.materials.length > 0
                                        ? `Vas a eliminar ${confirmDelete.materials.length} archivo${confirmDelete.materials.length === 1 ? '' : 's'}.`
                                        : `Vas a eliminar ${confirmDelete.folders.length} carpeta${confirmDelete.folders.length === 1 ? '' : 's'}.`, ' ', "Esta acci\u00F3n no se puede deshacer."] }), deleteError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: deleteError })), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "secondary", onClick: () => {
                                        setConfirmDelete(null);
                                        setDeleteError(null);
                                    }, children: "Cancelar" }), _jsx(Button, { variant: "danger", onClick: handleConfirmDelete, loading: deleteMaterial.isPending || deleteFolder.isPending, children: "Eliminar" })] })] })) })] }));
}
function FolderContents(props) {
    const { visibleFolders, visibleFiles, currentFolderId, currentLabel, selectedFolders, selectedFiles, previewBusyId, onOpenFolder, onSelectFolder, onSelectFile, onRenameFolder, onDeleteFolder, onPermisosFolder, onPermisosProfesoresFolder, onPreview, onPermissions, onRenameMaterial, onMoveMaterial, onDeleteMaterial, onUploadClick, onCreateFolder, } = props;
    const isEmpty = visibleFolders.length === 0 && visibleFiles.length === 0;
    if (isEmpty) {
        return (_jsxs("div", { className: "rounded-2xl border-2 border-dashed p-10 text-center", style: { borderColor: 'var(--border)' }, children: [_jsx("p", { className: "text-sm font-medium", style: { color: 'var(--fg)' }, children: currentFolderId ? `${currentLabel} está vacía` : 'No hay carpetas todavía' }), _jsx("p", { className: "mt-1 text-sm", style: { color: 'var(--fg-2)' }, children: currentFolderId
                        ? 'Arrastra archivos aquí o usa los botones de arriba.'
                        : 'Crea tu primera carpeta para empezar a organizar materiales.' }), _jsxs("div", { className: "mt-4 flex justify-center gap-2", children: [_jsx(Button, { variant: "secondary", onClick: onCreateFolder, children: "+ Carpeta" }), currentFolderId && _jsx(Button, { onClick: onUploadClick, children: "+ Subir archivo" })] })] }));
    }
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [visibleFolders.length > 0 && (_jsxs("section", { children: [_jsx("h3", { className: "mb-2 text-xs font-semibold uppercase tracking-wide", style: { color: 'var(--fg-2)' }, children: "Carpetas" }), _jsx("div", { className: "grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3", children: visibleFolders.map((node) => (_jsx(FolderCard, { node: node, selected: selectedFolders.has(node.folder.id), onOpen: () => onOpenFolder(node.folder.id), onSelectChange: (sel) => onSelectFolder(node.folder.id, sel), onRename: () => onRenameFolder(node.folder), onDelete: () => onDeleteFolder(node.folder), onPermisos: () => onPermisosFolder(node.folder), onPermisosProfesores: () => onPermisosProfesoresFolder(node.folder) }, node.folder.id))) })] })), visibleFiles.length > 0 && (_jsxs("section", { children: [_jsx("h3", { className: "mb-2 text-xs font-semibold uppercase tracking-wide", style: { color: 'var(--fg-2)' }, children: "Archivos" }), _jsx("div", { className: "flex flex-col gap-2", children: visibleFiles.map((m) => (_jsx(FileRow, { material: m, selected: selectedFiles.has(m.id), busy: previewBusyId === m.id, onSelectChange: (sel) => onSelectFile(m.id, sel), onPreview: () => onPreview(m), onPermissions: () => onPermissions(m), onRename: () => onRenameMaterial(m), onMove: () => onMoveMaterial(m), onDelete: () => onDeleteMaterial(m) }, m.id))) })] }))] }));
}
// ─────────────────────────────────────────────────────────────────────────────
function ancestorsString(folderId, index) {
    const chain = [];
    let cur = index.get(folderId);
    while (cur) {
        chain.unshift(cur.name);
        cur = cur.parent_id ? index.get(cur.parent_id) : undefined;
    }
    return chain.join(' / ');
}
function extensionOf(mime) {
    if (mime === 'application/pdf')
        return 'pdf';
    if (mime === 'application/msword')
        return 'doc';
    if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
        return 'docx';
    return '';
}
//# sourceMappingURL=MaterialsAdminPage.js.map